import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { fetchHistory } from '../../store/thunks/historyThunk';
import style from './History.module.css';
import CallDetailsModal from './components/CallDetailsModal/CallDetailsModal';
import ControlPanel from './components/ControlPanel/ControlPanel';
import { HistoryList } from './components/HistoryList/HistoryList';
import { TranslationHistoryItem } from './helpers';

const History = () => {
  const dispatch: AppDispatch = useDispatch();
  const { history, loading, hasNextPage } = useSelector(
    (state: RootState) => state.history,
  );

  const [data, setData] = useState<TranslationHistoryItem[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dateValues, setDateValues] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: '', endDate: '' });
  const [sortField, setSortField] = useState<string>('date');
  // За замовчуванням — від нових до старих (date DESC).
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [debounceTimeout, setDebounceTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  // Обрана картка дзвінка (модалка з усіма даними дзвінка).
  const [selectedCall, setSelectedCall] =
    useState<TranslationHistoryItem | null>(null);

  const isFirstLoad = useRef(true);

  const fetchData = () => {
    const adjustedEndDate = dateValues.endDate
      ? dayjs(dateValues.endDate).add(1, 'day').toISOString()
      : undefined;

    dispatch(
      fetchHistory(
        pageIndex,
        15,
        searchQuery,
        dateValues.startDate
          ? new Date(dateValues.startDate).toISOString()
          : undefined,
        adjustedEndDate,
        sortDirection,
        sortField,
      ),
    );
  };

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    setPageIndex(1);
    setData([]);
  }, [searchQuery, dateValues, sortField, sortDirection]);

  useEffect(() => {
    if (pageIndex > 1) {
      fetchData();
    }
  }, [pageIndex]);

  useEffect(() => {
    // #31: показуємо й записи без клієнта (раніше фільтрувались і «зникали»).
    // Дедуплікація за стійким складеним ключем (клієнт або зовнішній + перекладач + час).
    const keyOf = (i: TranslationHistoryItem) =>
      `${i?.createdAt}|${i?.translator?.id ?? ''}|${i?.client?.id ?? i?.external?.id ?? ''}`;
    setData((prev) => {
      if (pageIndex === 1) {
        return history;
      }
      const seen = new Set(prev.map(keyOf));
      const newData = history.filter((item) => !seen.has(keyOf(item)));
      return [...prev, ...newData];
    });
  }, [history, pageIndex]);

  useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(() => {
      setPageIndex(1);
      setData([]);
      fetchData();
    }, 1000);

    setDebounceTimeout(timeout);

    return () => clearTimeout(timeout);
  }, [searchQuery, dateValues, sortField, sortDirection]);

  const fetchMoreData = () => {
    if (!loading && hasNextPage) {
      setPageIndex((prev) => prev + 1);
    }
  };

  const handleChangeSort = (field: string) => {
    setPageIndex(1);
    setData([]);
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === 'asc' ? 'desc' : 'asc',
      );
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleChangeSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleChangeDate = (dates: { startDate: string; endDate: string }) => {
    setDateValues(dates);
  };

  return (
    <div className={style.history_container}>
      <ControlPanel
        searchQuery={searchQuery}
        setSearchQuery={handleChangeSearch}
        dateValues={dateValues}
        setDateValues={handleChangeDate}
      />
      <HistoryList
        handleChangeSort={handleChangeSort}
        fetchMoreData={fetchMoreData}
        data={data}
        hasNextPage={hasNextPage}
        sortDirection={sortDirection}
        sortField={sortField}
        loading={loading}
        onRowClick={setSelectedCall}
      />
      {selectedCall && (
        <CallDetailsModal
          item={selectedCall}
          onClose={() => setSelectedCall(null)}
        />
      )}
    </div>
  );
};

export default History;
